'use client';

import { useRouter, useSearchParams } from 'next/navigation';

import { PlayerScoreStatus, PlayerStatusHeader } from '@/components/molecules';
import { PlayerOverallStatusSkeleton } from '@/components/organisms/skeletons/PlayerOverallStatusSkeleton';
import { usePlayerStatsQuery } from '@/hooks/player/usePlayerStatsQuery';

interface PlayerOverallStatusProps {
	teamLeagueId: string;
	seasonId: string;
	playerId: string;
}

enum FilterKeys {
	OverallPeriod = 'overall_period',
	WinLossPeriod = 'win_loss_period'
}

export const PlayerOverallStatus = ({ teamLeagueId, seasonId, playerId }: PlayerOverallStatusProps) => {
	const router = useRouter();
	const searchParams = useSearchParams();
	const overallPeriod = searchParams.get(FilterKeys.OverallPeriod) || '';
	const winLossPeriod = searchParams.get(FilterKeys.WinLossPeriod) || '';
	const { data: playerStats, isFetching } = usePlayerStatsQuery(teamLeagueId, seasonId, playerId);

	if (isFetching || !playerStats) {
		return <PlayerOverallStatusSkeleton />;
	}

	if (playerStats?.statusCode !== 200) {
		return null;
	}

	const overallAnalytics = playerStats?.data?.overallAnalytics;
	const winLossAnalytics = playerStats?.data?.winLossAnalytics;

	const overallOptions = [{ value: '2025', label: '2025' }];
	const winLossOptions = [{ value: '2025', label: '2025' }];

	const handleFilterChange = (key: FilterKeys, value: string) => {
		const params = new URLSearchParams(searchParams.toString());

		value ? params.set(key, value) : params.delete(key);

		router.push(`?${params.toString()}`, { scroll: false });
	};

	return (
		<>
			<div className="w-full max-w-[350px] rounded-xl border border-gray-300 sm:max-w-[400px]">
				<PlayerStatusHeader
					title="Overall"
					options={overallOptions}
					selectedValue={overallPeriod}
					onChange={(value) => handleFilterChange(FilterKeys.OverallPeriod, value)}
				/>
				<PlayerScoreStatus statusType="overallAnalytics" overallAnalytics={overallAnalytics!} />
			</div>
			<div className="w-full max-w-[350px] rounded-xl border border-gray-300 sm:max-w-[400px]">
				<PlayerStatusHeader
					title="Win/Loss"
					options={winLossOptions}
					selectedValue={winLossPeriod}
					onChange={(value) => handleFilterChange(FilterKeys.WinLossPeriod, value)}
				/>
				<PlayerScoreStatus statusType="winLossAnalytics" winLossAnalytics={winLossAnalytics!} />
			</div>
		</>
	);
};
